import isNil from 'lodash/isNil';
import { StatsigClient, StatsigUser, StatsigOptions, DynamicConfig } from '@statsig/js-client';
import { LocalOverrideAdapter } from '@statsig/js-local-overrides';

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { IResponseCompany, IResponseUser } from '@services/company.interface';
import { STATSIG_OVERRIDES_KEY, IStatsigOverrides } from '@app/statsig-overlay/statsig-overlay.interface';

import { logger } from '@lib/datadog';
import { ENV, getRegion } from '@lib/environments.const';

import { LoadTimeLogger, MARKERS, MEASURES_NAMES } from '@lib/performance-logging';

const performanceLogger: LoadTimeLogger = new LoadTimeLogger();
const overrideAdapter: LocalOverrideAdapter = new LocalOverrideAdapter();

const statsigOverrides: IStatsigOverrides | null = loadOverridesFromSessionStorage();
let isJsonValid: boolean = true;

export enum STATSIG_ENVIRONEMNTS {
  DEVELOPMENT = 'development',
  PREVIEW = 'preview',
  STAGING = 'staging',
  PRODUCTION = 'production',
}

const mapEnvForStatsigConfig = (): STATSIG_ENVIRONEMNTS => {
  const selectedEnvrionment: string | null = statsigOverrides?.statsigEnvironmentSelected;

  if (selectedEnvrionment) {
    return selectedEnvrionment as STATSIG_ENVIRONEMNTS;
  }
  if (ENVIRONMENT === ENV.LOCAL) return STATSIG_ENVIRONEMNTS.DEVELOPMENT;
  else if (ENVIRONMENT === ENV.LOCAL_PREVIEW) return STATSIG_ENVIRONEMNTS.PREVIEW;
  return ENVIRONMENT as STATSIG_ENVIRONEMNTS;
};

let statsigReadyPromise: Promise<any>;
let statsigClient: StatsigClient;

function loadOverridesFromSessionStorage(): IStatsigOverrides | null {
  const overridesString: string = sessionStorage.getItem(STATSIG_OVERRIDES_KEY);

  if (overridesString) return JSON.parse(overridesString) as IStatsigOverrides;
  return null;
}

function applyStatsigOverrides(): void {
  const gateOverrides: Record<string, boolean> = statsigOverrides?.gate ?? {};
  const configOverrides: Record<string, string> = statsigOverrides?.dynamicConfig ?? {};

  Object.keys(gateOverrides).forEach((gate) => {
    overrideAdapter.overrideGate(gate, gateOverrides[gate]);
  });

  Object.keys(configOverrides).forEach((config) => {
    try {
      const parsedConfig = JSON.parse(configOverrides[config]);
      overrideAdapter.overrideDynamicConfig(config, parsedConfig);
    } catch (e) {
      isJsonValid = false;
      delete statsigOverrides?.dynamicConfig[config];
    }
  });
}

export function initFeatureFlagStatsig(user: IResponseUser, company: IResponseCompany): Promise<void> {
  const userKey: number = user.id;
  const statsigUser: StatsigUser = {
    userID: userKey.toString(),
    privateAttributes: {
      email: user.email,
    },
    custom: {
      region: getRegion(),
      company_id: null,
      company_kt_id: null,
      company_subscription_plan: null,
      company_segment_type: null,
      company_segment_type_sf: null,
      company_sf_account_sub_segment: null,
      company_enabled_features: null,
      type: 'user',
      composite_fleet_size: 0,
      country: null,
    },
    customIDs: {
      company_id: null,
    },
  };

  if (!isNil(company?.id)) {
    Object.assign(statsigUser.custom, {
      company_id: company.id,
      company_kt_id: company.company_id,
      company_subscription_plan: company.subscription_plan,
      company_segment_type: company.segment_type,
      company_segment_type_sf: company.segment_type_sf,
      company_sf_account_sub_segment: company.sf_account_sub_segment,
      company_enabled_features: company.enabled_features,
      composite_fleet_size: company.composite_fleet_size,
      country: company.country,
    });
    Object.assign(statsigUser.customIDs, {
      company_id: company.id.toString(),
    });
  }

  const initTimeoutSeconds: number = 6;
  const options: StatsigOptions = {
    networkConfig: {
      api: STATSIG_PROXY_URL,
    },
    environment: { tier: mapEnvForStatsigConfig() },
    overrideAdapter,
  };
  try {
    window.performance.mark(MARKERS.STATSIG_INIT_CALL_START);
    performanceLogger.measureFromMarker(
      MARKERS.BOOT_START,
      MARKERS.STATSIG_INIT_CALL_START,
      MEASURES_NAMES.STATSIG_INIT_CALL_START_TIME,
      { clearMarkers: false, flush: true },
    );
    window.performance.clearMarks(MARKERS.STATSIG_INIT_CALL_START);
  } catch (error) {
    console.log(error);
  }

  statsigClient = new StatsigClient(STATSIG_CLIENT_ID, statsigUser, options);

  statsigClient.on('values_updated', evt => {
    if (evt.status && evt.status === 'Ready') {
      if ((window as any).IS_STATSIG_INIT_FAILED) {
        (window as any).IS_STATSIG_INIT_FAILED = undefined;
      }
    }
  });

  statsigClient.on('initialization_failure', evt => {
    (window as any).IS_STATSIG_INIT_FAILED = true;
    (window as any).STATSIG_INIT_TIME = 0;
  });

  statsigClient.on('error', evt => {
    console.log('error in statsig', evt);
  });

  applyStatsigOverrides();
  statsigReadyPromise = statsigClient.initializeAsync({
    timeoutMs: initTimeoutSeconds * 1000,
  });

  statsigReadyPromise.then((event) => {
    const w = window as any;
    if (event && event.error) {
      w.IS_STATSIG_INIT_FAILED = true;
      w.STATSIG_INIT_TIME = 0;
      console.error('Statsig initialization failed', event.error);
    } else {
      w.STATSIG_INIT_TIME = event.duration;
    }
  });

  return statsigReadyPromise;
}

@Injectable({ providedIn: 'root' })
export class StatsigService {
  public trackFeatureFlags: Record<string, boolean> = {};
  public featureFlagsUsedSubject: BehaviorSubject<Record<string, boolean>> =
    new BehaviorSubject<Record<string, boolean>> (this.trackFeatureFlags);
  public featureFlagsUsed$: Observable<Record<string, boolean>> =
    this.featureFlagsUsedSubject.asObservable();

  public trackDynamicConfigs: Record<string, string> = {};
  public dynamicConfigsUsedSubject: BehaviorSubject<Record<string, string>> =
    new BehaviorSubject<Record<string, string>> (this.trackDynamicConfigs);
  public dynamicConfigsUsed$: Observable<Record<string, string>> =
    this.dynamicConfigsUsedSubject.asObservable();

  private allStatsigOverrides: IStatsigOverrides = statsigOverrides;
  private currentStatsigEnv: STATSIG_ENVIRONEMNTS = mapEnvForStatsigConfig();
  private isHighlightEnabled: boolean = statsigOverrides?.ishighlighterEnabled ?? false;
  private isDynamicConfigJsonValid: boolean = isJsonValid;

  public get statsigOverrides(): IStatsigOverrides {
    return this.allStatsigOverrides;
  }

  public get statsigEnvironment(): STATSIG_ENVIRONEMNTS {
    return this.currentStatsigEnv;
  }

  public get isHighlightActive(): boolean {
    return this.isHighlightEnabled;
  }

  public get isJsonErrorBannerDisplayed(): boolean {
    return !this.isDynamicConfigJsonValid;
  }

  /**
   * The LaunchDarkly client requires making a network request to get the
   * list of feature flags for the user. In our app initialization, we will
   * call this method to ensure that downstream clients of FeatureFlag have
   * the loaded set of feature flags.
   */
  public waitUntilReady(): Promise<void> {
    return statsigReadyPromise;
  }

  /**
   * @param flagKey Feature gate key name to evaluate
   * @returns boolean indicating whether the feature flag is enabled or disabled
   */
  public evaluateGate(flagKey: string): boolean {
    const featureFlagState: boolean = StatsigClient.instance(STATSIG_CLIENT_ID).checkGate(flagKey);
    const featureFlagName: string = flagKey;
    
    if (!this.trackFeatureFlags.hasOwnProperty(featureFlagName)) {
      this.trackFeatureFlags[featureFlagName] = featureFlagState;
    }

    return featureFlagState;
  }

  /**
   * @param flagKey Dynamic config key string to evaluate
   * @returns DynamicConfig object
   */
  public evaluateDynamicConfig(flagKey: string): DynamicConfig {
    const dynamicConfigObject: DynamicConfig = StatsigClient.instance(STATSIG_CLIENT_ID).getDynamicConfig(flagKey);
    
    if (!this.trackDynamicConfigs.hasOwnProperty(flagKey)) {
      this.trackDynamicConfigs[flagKey] = JSON.stringify(dynamicConfigObject.value, null, 2);
    }
    return dynamicConfigObject;
  }

  /**
   * @param experimentName Name of the experiment to get
   * @returns DynamicConfig object
   * Statsig offer two APIs to run A/B/n experiments; Layers & Experiments, which we can use to run A/B/n experiments.
   */
  public getExperiment(experimentName: string): DynamicConfig {
    return StatsigClient.instance(STATSIG_CLIENT_ID).getExperiment(experimentName);
  }

  /**
   * @param eventName Name string of the event to log
   * @param value  Optional string value of the event to log
   * @param metaData Optional object to log addtional details
   * @returns void
   */
  public logEvent(eventName: string, value?: string, metaData?: Record<string, string>): void {
    StatsigClient.instance(STATSIG_CLIENT_ID).logEvent(eventName, value, metaData);
  }

  /**
   * @returns void
   */
  public shutdown(): void {
    try {
      StatsigClient.instance(STATSIG_CLIENT_ID).shutdown();
    } catch (error) {
      console.log(error);
    }
  }

  public updateFeatureFlagUsed(): void {
    this.featureFlagsUsedSubject.next({ ...this.trackFeatureFlags });
    this.dynamicConfigsUsedSubject.next({...this.trackDynamicConfigs });
  }

  public clearMap(): void {
    this.trackFeatureFlags = {};
    this.trackDynamicConfigs = {};
  }
}
